import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Community.css';

function Community() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [activeTab, setActiveTab] = useState('feed');
  const [showChat, setShowChat] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postContent, setPostContent] = useState('');
  const chatEndRef = useRef(null);

  // Sample data
  const [posts, setPosts] = useState([
    {
      id: 1,
      author: { name: 'Sarah Johnson', avatar: '👩‍🎓', major: 'Computer Science', year: 'Junior' },
      content: 'Just aced my Data Structures exam! 🎉 Anyone want to form a study group for Algorithms next semester?',
      likes: 24,
      comments: 8,
      time: '2 hours ago',
      liked: false
    },
    {
      id: 2,
      author: { name: 'Mike Chen', avatar: '👨‍🎓', major: 'Business', year: 'Senior' },
      content: 'Looking for roommates for next semester! 2BR apartment near campus, $600/month. DM if interested 🏠',
      likes: 15,
      comments: 12,
      time: '4 hours ago',
      liked: true
    },
    {
      id: 3,
      author: { name: 'Emily Rodriguez', avatar: '🧑‍🎓', major: 'Psychology', year: 'Sophomore' },
      content: 'The new coffee shop on Kennedy Blvd is amazing! Perfect study spot ☕📚',
      image: '☕',
      likes: 42,
      comments: 6,
      time: '6 hours ago',
      liked: false
    }
  ]);

  const communities = [
    { id: 1, name: 'CS Majors', members: 234, icon: '💻', color: '#667eea', joined: true },
    { id: 2, name: 'Business Club', members: 189, icon: '📊', color: '#f97316', joined: false },
    { id: 3, name: 'Pre-Med Society', members: 156, icon: '🩺', color: '#ef4444', joined: true },
    { id: 4, name: 'International Students (ISA)', members: 312, icon: '🌍', color: '#06b6d4', joined: true, link: '/community/isa' },
    { id: 5, name: 'Art & Design', members: 98, icon: '🎨', color: '#ec4899', joined: false },
    { id: 6, name: 'Sports & Fitness', members: 267, icon: '⚽', color: '#22c55e', joined: true },
    { id: 7, name: 'College Zone', members: 520, icon: '🎓', color: '#8b5cf6', joined: true, link: '/college-zone', featured: true }
  ];

  const students = [
    { id: 1, name: 'Alex Thompson', avatar: '👨‍🎓', major: 'Computer Science', year: 'Junior', mutual: 5, connected: false },
    { id: 2, name: 'Jessica Lee', avatar: '👩‍🎓', major: 'Biology', year: 'Senior', mutual: 3, connected: true },
    { id: 3, name: 'David Kim', avatar: '🧑‍🎓', major: 'Engineering', year: 'Sophomore', mutual: 8, connected: false },
    { id: 4, name: 'Maria Garcia', avatar: '👩‍🎓', major: 'Psychology', year: 'Freshman', mutual: 2, connected: true },
    { id: 5, name: 'James Wilson', avatar: '👨‍🎓', major: 'Business', year: 'Junior', mutual: 6, connected: false }
  ];

  const [chats, setChats] = useState([
    {
      id: 1,
      user: { name: 'Jessica Lee', avatar: '👩‍🎓', online: true },
      lastMessage: 'Hey! Are you going to the study session tomorrow?',
      time: '2m ago',
      unread: 2,
      messages: [
        { id: 1, sender: 'them', text: 'Hey! How are you?', time: '10:30 AM' },
        { id: 2, sender: 'me', text: 'I\'m good! Just studying for finals', time: '10:32 AM' },
        { id: 3, sender: 'them', text: 'Same here! It\'s rough 😅', time: '10:33 AM' },
        { id: 4, sender: 'them', text: 'Hey! Are you going to the study session tomorrow?', time: '10:35 AM' }
      ]
    },
    {
      id: 2,
      user: { name: 'Maria Garcia', avatar: '👩‍🎓', online: false },
      lastMessage: 'Thanks for the notes!',
      time: '1h ago',
      unread: 0,
      messages: [
        { id: 1, sender: 'me', text: 'Here are the notes from today\'s class', time: '9:00 AM' },
        { id: 2, sender: 'them', text: 'Thanks for the notes!', time: '9:15 AM' }
      ]
    },
    {
      id: 3,
      user: { name: 'CS Study Group', avatar: '💻', online: true, isGroup: true },
      lastMessage: 'Alex: Meeting at library at 3pm',
      time: '30m ago',
      unread: 5,
      messages: [
        { id: 1, sender: 'Alex', text: 'Anyone free to study today?', time: '2:00 PM' },
        { id: 2, sender: 'Jessica', text: 'I\'m in!', time: '2:05 PM' },
        { id: 3, sender: 'Alex', text: 'Meeting at library at 3pm', time: '2:10 PM' }
      ]
    }
  ]);

  const handleLike = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          liked: !post.liked,
          likes: post.liked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  const handleSendMessage = () => {
    if (!message.trim() || !selectedChat) return;

    const newMessage = {
      id: Date.now(),
      sender: 'me',
      text: message,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setChats(chats.map(chat => {
      if (chat.id === selectedChat.id) {
        return {
          ...chat,
          messages: [...chat.messages, newMessage],
          lastMessage: message,
          time: 'Just now'
        };
      }
      return chat;
    }));

    setMessage('');
  };

  const handleCreatePost = () => {
    if (!postContent.trim()) return;

    const newPost = {
      id: Date.now(),
      author: {
        name: user?.name || 'You',
        avatar: '👤',
        major: 'SPU Student',
        year: ''
      },
      content: postContent,
      likes: 0,
      comments: 0,
      time: 'Just now',
      liked: false
    };

    setPosts([newPost, ...posts]);
    setPostContent('');
    setShowCreatePost(false);
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedChat?.messages]);

  return (
    <div className="community-page">
      {/* Header */}
      <header className="community-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ←
        </button>
        <div className="header-title">
          <h1>Community</h1>
          <span className="header-subtitle">Connect with SPU Students</span>
        </div>
        <button className="chat-toggle" onClick={() => setShowChat(!showChat)}>
          💬
          {chats.reduce((acc, c) => acc + c.unread, 0) > 0 && (
            <span className="chat-badge">{chats.reduce((acc, c) => acc + c.unread, 0)}</span>
          )}
        </button>
      </header>

      {/* Tabs */}
      <div className="community-tabs">
        <button
          className={`tab ${activeTab === 'feed' ? 'active' : ''}`}
          onClick={() => setActiveTab('feed')}
        >
          📰 Feed
        </button>
        <button
          className={`tab ${activeTab === 'communities' ? 'active' : ''}`}
          onClick={() => setActiveTab('communities')}
        >
          👥 Groups
        </button>
        <button
          className={`tab ${activeTab === 'people' ? 'active' : ''}`}
          onClick={() => setActiveTab('people')}
        >
          🔗 Connect
        </button>
      </div>

      {/* Content */}
      <div className="community-content">
        {/* Feed Tab */}
        {activeTab === 'feed' && (
          <div className="feed-section">
            {/* Create Post */}
            <div className="create-post-card" onClick={() => setShowCreatePost(true)}>
              <div className="create-post-avatar">👤</div>
              <div className="create-post-input">What's on your mind?</div>
              <button className="create-post-btn">Post</button>
            </div>

            {/* Stories/Highlights */}
            <div className="stories-row">
              <div className="story add-story">
                <div className="story-avatar">+</div>
                <span>Add Story</span>
              </div>
              {students.slice(0, 4).map(student => (
                <div key={student.id} className="story">
                  <div className="story-avatar has-story">{student.avatar}</div>
                  <span>{student.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>

            {/* Posts */}
            <div className="posts-list">
              {posts.map(post => (
                <div key={post.id} className="post-card">
                  <div className="post-header">
                    <div className="post-avatar">{post.author.avatar}</div>
                    <div className="post-author-info">
                      <span className="post-author-name">{post.author.name}</span>
                      <span className="post-author-details">{post.author.major} • {post.author.year}</span>
                    </div>
                    <span className="post-time">{post.time}</span>
                  </div>
                  <div className="post-content">
                    {post.content}
                    {post.image && <div className="post-image">{post.image}</div>}
                  </div>
                  <div className="post-actions">
                    <button
                      className={`post-action ${post.liked ? 'liked' : ''}`}
                      onClick={() => handleLike(post.id)}
                    >
                      {post.liked ? '❤️' : '🤍'} {post.likes}
                    </button>
                    <button className="post-action">
                      💬 {post.comments}
                    </button>
                    <button className="post-action">
                      🔗 Share
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Communities Tab */}
        {activeTab === 'communities' && (
          <div className="communities-section">
            {/* Featured Communities */}
            <div className="section-header">
              <h2>Featured</h2>
            </div>
            <div className="featured-communities">
              {communities.filter(c => c.featured).map(community => (
                <div
                  key={community.id}
                  className="community-card featured"
                  onClick={() => community.link && navigate(community.link)}
                  style={{ cursor: community.link ? 'pointer' : 'default' }}
                >
                  <div className="community-banner" style={{ background: `linear-gradient(135deg, ${community.color}, ${community.color}80)` }}>
                    <span className="community-icon-large">{community.icon}</span>
                  </div>
                  <div className="community-info">
                    <h3>{community.name}</h3>
                    <span>{community.members} members</span>
                  </div>
                  <button className="community-btn featured-btn">Explore →</button>
                </div>
              ))}
            </div>

            <div className="section-header">
              <h2>Your Communities</h2>
            </div>
            <div className="communities-grid">
              {communities.filter(c => c.joined && !c.featured).map(community => (
                <div
                  key={community.id}
                  className="community-card joined"
                  onClick={() => community.link && navigate(community.link)}
                  style={{ cursor: community.link ? 'pointer' : 'default' }}
                >
                  <div className="community-icon" style={{ backgroundColor: community.color + '20' }}>
                    {community.icon}
                  </div>
                  <div className="community-info">
                    <h3>{community.name}</h3>
                    <span>{community.members} members</span>
                  </div>
                  <button className="community-btn joined">Joined ✓</button>
                </div>
              ))}
            </div>

            <div className="section-header">
              <h2>Discover Communities</h2>
            </div>
            <div className="communities-grid">
              {communities.filter(c => !c.joined).map(community => (
                <div key={community.id} className="community-card">
                  <div className="community-icon" style={{ backgroundColor: community.color + '20' }}>
                    {community.icon}
                  </div>
                  <div className="community-info">
                    <h3>{community.name}</h3>
                    <span>{community.members} members</span>
                  </div>
                  <button className="community-btn">Join</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* People Tab */}
        {activeTab === 'people' && (
          <div className="people-section">
            {/* Search */}
            <div className="people-search">
              <span className="search-icon">🔍</span>
              <input type="text" placeholder="Search students..." />
            </div>

            <div className="section-header">
              <h2>Your Connections</h2>
              <span className="connection-count">{students.filter(s => s.connected).length} connections</span>
            </div>
            <div className="people-list">
              {students.filter(s => s.connected).map(student => (
                <div key={student.id} className="person-card connected">
                  <div className="person-avatar">{student.avatar}</div>
                  <div className="person-info">
                    <h3>{student.name}</h3>
                    <span>{student.major} • {student.year}</span>
                  </div>
                  <button className="message-btn" onClick={() => {
                    setSelectedChat(chats.find(c => c.user.name === student.name) || {
                      id: Date.now(),
                      user: { name: student.name, avatar: student.avatar, online: true },
                      messages: [],
                      lastMessage: '',
                      time: ''
                    });
                    setShowChat(true);
                  }}>
                    💬
                  </button>
                </div>
              ))}
            </div>

            <div className="section-header">
              <h2>People You May Know</h2>
            </div>
            <div className="people-list">
              {students.filter(s => !s.connected).map(student => (
                <div key={student.id} className="person-card">
                  <div className="person-avatar">{student.avatar}</div>
                  <div className="person-info">
                    <h3>{student.name}</h3>
                    <span>{student.major} • {student.year}</span>
                    <span className="mutual-friends">{student.mutual} mutual connections</span>
                  </div>
                  <button className="connect-btn">Connect</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chat Sidebar */}
      <div className={`chat-sidebar ${showChat ? 'open' : ''}`}>
        <div className="chat-sidebar-header">
          <h2>Messages</h2>
          <button className="close-chat" onClick={() => setShowChat(false)}>×</button>
        </div>

        {!selectedChat ? (
          <div className="chat-list">
            {chats.map(chat => (
              <div
                key={chat.id}
                className={`chat-item ${chat.unread > 0 ? 'unread' : ''}`}
                onClick={() => setSelectedChat(chat)}
              >
                <div className="chat-avatar">
                  {chat.user.avatar}
                  {chat.user.online && <span className="online-dot"></span>}
                </div>
                <div className="chat-preview">
                  <div className="chat-name">{chat.user.name}</div>
                  <div className="chat-last-message">{chat.lastMessage}</div>
                </div>
                <div className="chat-meta">
                  <span className="chat-time">{chat.time}</span>
                  {chat.unread > 0 && <span className="unread-badge">{chat.unread}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chat-conversation">
            <div className="conversation-header">
              <button className="back-to-chats" onClick={() => setSelectedChat(null)}>←</button>
              <div className="conversation-user">
                <div className="chat-avatar">
                  {selectedChat.user.avatar}
                  {selectedChat.user.online && <span className="online-dot"></span>}
                </div>
                <div className="conversation-info">
                  <span className="conversation-name">{selectedChat.user.name}</span>
                  <span className="conversation-status">
                    {selectedChat.user.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>

            <div className="messages-container">
              {selectedChat.messages.map(msg => (
                <div key={msg.id} className={`message ${msg.sender === 'me' ? 'sent' : 'received'}`}>
                  {msg.sender !== 'me' && msg.sender !== 'them' && (
                    <span className="message-sender">{msg.sender}</span>
                  )}
                  <div className="message-bubble">
                    {msg.text}
                  </div>
                  <span className="message-time">{msg.time}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="message-input-container">
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button className="send-btn" onClick={handleSendMessage}>
                ➤
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="modal-overlay" onClick={() => setShowCreatePost(false)}>
          <div className="create-post-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Post</h2>
              <button className="close-modal" onClick={() => setShowCreatePost(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="post-author-row">
                <div className="post-avatar">👤</div>
                <div className="post-author-info">
                  <span className="post-author-name">{user?.name || 'You'}</span>
                  <span className="post-visibility">🌍 Public</span>
                </div>
              </div>
              <textarea
                placeholder="What's on your mind?"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                autoFocus
              />
              <div className="post-options">
                <button className="post-option">📷 Photo</button>
                <button className="post-option">🎥 Video</button>
                <button className="post-option">📍 Location</button>
                <button className="post-option">😀 Feeling</button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="post-submit-btn"
                disabled={!postContent.trim()}
                onClick={handleCreatePost}
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Community;
